import React, { useEffect, useState, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './ProfilePage.css';
import './Pages.css';

// Add animations for the modal
const modalAnimations = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

// Inject styles into document head
if (!document.querySelector('#copyModalAnimations')) {
  const style = document.createElement('style');
  style.id = 'copyModalAnimations';
  style.textContent = modalAnimations;
  document.head.appendChild(style);
}

const GalleryPage = () => {
  const [publicGames, setPublicGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [expandedImages, setExpandedImages] = useState(new Set());
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  // const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [likesData, setLikesData] = useState({});
  // const [navigate] = useNavigate();
  const { currentUser } = useAuth();

  // Copy game modal state
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyGameData, setCopyGameData] = useState(null);
  const [copyGameName, setCopyGameName] = useState('');

  // פונקציה לטעינת נתוני לייקים
  const fetchLikesData = useCallback(async (gameStateIds) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/likes/batch', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gameStateIds })
      });

      if (response.ok) {
        const data = await response.json();
        const likesMap = {};
        data.likesData.forEach(item => {
          likesMap[item.gameStateId] = {
            likeCount: item.likeCount,
            userLiked: item.userLiked
          };
        });
        setLikesData(likesMap);
      }
    } catch (error) {
      console.error('Error fetching likes data:', error);
    }
  }, []);

  // פונקציה לטעינת משחקים ציבוריים
  const fetchPublicGames = useCallback(async (pageNum = 1, search = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: pageNum,
        limit: 20,
        search: search.trim()
      });

      const response = await fetch(`/api/game-states/public/all?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      const data = await response.json();
      const games = data.gameStates || [];
      setPublicGames(games);
      setPagination(data.pagination);
      
      // טען נתוני לייקים למשחקים
      if (games.length > 0) {
        await fetchLikesData(games.map(game => game.game_state_id));
      }
    } catch (err) {
      console.error('Error fetching public games:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchLikesData]);

  useEffect(() => {
    if (currentUser) {
      fetchPublicGames(1, searchTerm);
    }
  }, [currentUser, fetchPublicGames, searchTerm]);

  // טעינה מחדש כשמשנים חיפוש
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (currentUser) {
        setPage(1);
        fetchPublicGames(1, searchTerm);
      }
    }, 500); // 500ms delay לחיפוש

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, currentUser, fetchPublicGames]);

  // const handleLoadGame = (game) => {
  //   navigate(`/app?gameId=${game.game_state_id}`);
  // };

  const handleToggleImageExpand = (gameId) => {
    setExpandedImages(prev => {
      const newSet = new Set(prev);
      const placeholderElement = document.querySelector(
        `[data-game-id="${gameId}"] .game-image-placeholder`
      );
      
      if (newSet.has(gameId)) {
        // Collapsing - reset height to auto
        newSet.delete(gameId);
        if (placeholderElement) {
          placeholderElement.style.height = '';
        }
      } else {
        // Expanding - calculate optimal height
        newSet.add(gameId);
        
        setTimeout(() => {
          const game = publicGames.find(g => g.game_state_id === gameId);
          if (game && game.thumbnail_url && placeholderElement) {
            const imageElement = new Image();
            imageElement.onload = () => {
              const aspectRatio = imageElement.naturalHeight / imageElement.naturalWidth;
              const calculatedHeight = 500 * aspectRatio;
              
              // Set calculated height for the expanded image
              placeholderElement.style.height = `${calculatedHeight}px`;
            };
            imageElement.src = game.thumbnail_url;
          }
        }, 100); // Small delay to ensure DOM update and CSS transition
      }
      return newSet;
    });
  };

  // const handleToggleDescriptionExpand = (gameId) => {
  //   setExpandedDescriptions(prev => {
  //     const newSet = new Set(prev);
  //     if (newSet.has(gameId)) {
  //       newSet.delete(gameId);
  //     } else {
  //       newSet.add(gameId);
  //     }
  //     return newSet;
  //   });
  // };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getGameMetadata = (game) => {
    try {
      return typeof game.metadata === 'string' 
        ? JSON.parse(game.metadata) 
        : game.metadata || {};
    } catch (e) {
      return {};
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchPublicGames(newPage, searchTerm);
  };

  // פונקציה לטיפול בלייק
  const handleToggleLike = async (gameStateId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await fetch(`/api/likes/toggle/${gameStateId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // עדכן את נתוני הלייקים באופן מקומי
        setLikesData(prev => ({
          ...prev,
          [gameStateId]: {
            likeCount: data.likeCount,
            userLiked: data.liked
          }
        }));
      } else {
        throw new Error('Failed to toggle like');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      setError('Failed to update like. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // פונקציה לשכפול משחק ציבורי
  const handleCopyPublicGame = async (gameStateId, gameName) => {
    // פתח את המודל המותאם אישית
    setCopyGameData({ gameStateId, gameName });
    setCopyGameName(`Copy of ${gameName}`);
    setShowCopyModal(true);
  };

  // פונקציה לטיפול בהגשת השם החדש
  const handleCopyModalSubmit = async () => {
    if (!copyGameName.trim()) {
      return; // לא לעשות כלום אם השם ריק
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login to copy games');
        setShowCopyModal(false);
        return;
      }

      const response = await fetch(`/api/game-states/public/${copyGameData.gameStateId}/copy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: copyGameName.trim(),
          description: `Copied from public gallery`
        })
      });

      if (response.ok) {
        setError(null);
        setShowCopyModal(false);
        
        // הצג הודעת הצלחה
        const successMessage = document.createElement('div');
        successMessage.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
          color: var(--text-color);
          padding: var(--spacing-xl);
          border-radius: 12px;
          border: 2px solid var(--accent-color);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
          z-index: 10000;
          font-family: var(--font-primary);
          font-weight: 600;
          text-align: center;
          max-width: 400px;
          animation: fadeInScale 0.3s ease-out;
        `;
        
        successMessage.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 16px;">
            <i class="fas fa-check-circle" style="color: var(--accent-color); font-size: 24px;"></i>
            <h3 style="margin: 0; font-size: 18px;">Game Copied Successfully!</h3>
          </div>
          <p style="margin: 0; font-family: var(--font-secondary); font-size: 14px; opacity: 0.9;">
            "${copyGameName}" has been added to your personal games collection.
          </p>
          <div style="margin-top: 20px;">
            <button id="viewMyGames" style="
              background: var(--accent-color);
              color: var(--background-color);
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              font-family: var(--font-primary);
              font-weight: 600;
              cursor: pointer;
              margin-right: 10px;
            ">View My Games</button>
            <button id="closeMessage" style="
              background: transparent;
              color: var(--text-color);
              border: 1px solid var(--text-color);
              padding: 10px 20px;
              border-radius: 6px;
              font-family: var(--font-primary);
              font-weight: 600;
              cursor: pointer;
            ">Close</button>
          </div>
        `;
        
        // הוסף אנימציה
        if (!document.querySelector('#fadeInScaleKeyframes')) {
          const style = document.createElement('style');
          style.id = 'fadeInScaleKeyframes';
          style.textContent = `
            @keyframes fadeInScale {
              from {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.8);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
            }
          `;
          document.head.appendChild(style);
        }
        
        document.body.appendChild(successMessage);
        
        // הוסף event listeners לכפתורים
        document.getElementById('viewMyGames').addEventListener('click', () => {
          document.body.removeChild(successMessage);
          window.location.href = '/profile';
        });
        
        document.getElementById('closeMessage').addEventListener('click', () => {
          document.body.removeChild(successMessage);
        });
        
        // הסר אוטומטית אחרי 5 שניות
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage);
          }
        }, 5000);
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to copy game');
      }
    } catch (error) {
      console.error('Error copying game:', error);
      setError(`Failed to copy game: ${error.message}`);
      setShowCopyModal(false);
      setTimeout(() => setError(null), 5000);
    }
  };

  // פונקציה לביטול מודל ההעתקה
  const handleCopyModalCancel = () => {
    setShowCopyModal(false);
    setCopyGameData(null);
    setCopyGameName('');
  };

  if (!currentUser) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <h1>Please log in to view the public gallery</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1><i className="fas fa-globe"></i> Public Games Gallery</h1>
          <div className="user-info">
            <h2>Discover amazing maps created by the community!</h2>
            <p>Total public games: {pagination?.total || 0}</p>
          </div>
        </div>

        <div className="profile-content">
          {/* Search Section */}
          <div className="games-section">
            <div className="section-header">
              <h3><i className="fas fa-search"></i> Search & Browse ({pagination?.total || 0} games)</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button 
                  className="toggle-descriptions-btn" 
                  onClick={() => setShowAllDescriptions(!showAllDescriptions)}
                  title={showAllDescriptions ? "Hide all descriptions" : "Show all descriptions"}
                  style={{
                    background: 'var(--primary-color)',
                    color: 'var(--text-color)',
                    border: '2px solid var(--primary-color)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-primary)',
                    fontWeight: '600',
                    fontSize: 'var(--font-size-sm)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minHeight: '36px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--secondary-color)';
                    e.target.style.borderColor = 'var(--secondary-color)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'var(--primary-color)';
                    e.target.style.borderColor = 'var(--primary-color)';
                  }}
                >
                  <i className={`fas ${showAllDescriptions ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  {showAllDescriptions ? 'Hide Descriptions' : 'Show Descriptions'}
                </button>
                <div className="gallery-search-container">
        <input 
          type="text" 
                  placeholder="Search by name or description..." 
          className="gallery-search"
          value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    border: '2px solid var(--border-color)',
                    background: 'var(--background-color)',
                    color: 'var(--text-color)',
                    fontFamily: 'var(--font-secondary)',
                    fontSize: 'var(--font-size-base)',
                    width: '300px'
                  }}
        />
      </div>
              </div>
            </div>
            
            {loading && <div className="loading">Loading public games...</div>}
            {error && <div className="error">Error: {error}</div>}
            
            {!loading && !error && publicGames.length === 0 && (
              <div className="no-games">
                {searchTerm ? (
                  <>
                    <span className="empty-icon"><i className="fas fa-search"></i></span>
                    <h3>No games found</h3>
                    <p>No public games found matching search "{searchTerm}"</p>
                  </>
                ) : (
                  <>
                    <span className="empty-icon"><i className="fas fa-globe"></i></span>
                    <h3>No public games yet</h3>
                    <p>Be the first to share a game with the community!</p>
                  </>
                )}
              </div>
            )}

            {!loading && !error && publicGames.length > 0 && (
              <div className="games-grid">
                {publicGames.map(game => {
                  const metadata = getGameMetadata(game);
                  
                  return (
                    <div key={game.game_state_id} className="game-card" data-game-id={game.game_state_id}>
                      <div className="game-content">
                        <div className="game-info-column">
                          <div className="game-top-row">
                            <h4 className="game-title">
                              {game.name}
                            </h4>
                            <div className="game-actions">
                              <button 
                                onClick={() => handleCopyPublicGame(game.game_state_id, game.name)}
                                className="copy-btn"
                                title="Copy Game to My Collection"
                                style={{
                                  height: 'var(--button-height-sm)',
                                  minHeight: 'var(--button-height-sm)',
                                  padding: 'var(--spacing-xs) var(--spacing-md)',
                                  border: '2px solid var(--primary-color)',
                                  backgroundColor: 'var(--primary-color)',
                                  color: 'var(--text-color)',
                                  borderRadius: 'var(--border-radius-sm)',
                                  fontSize: '1.2rem',
                                  fontFamily: 'var(--font-primary)',
                                  fontWeight: '600',
                                  lineHeight: '1',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  textDecoration: 'none',
                                  boxShadow: '0 2px 4px rgba(160, 44, 44, 0.3)',
                                  minWidth: '40px',
                                  gap: '6px'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'var(--secondary-color)';
                                  e.target.style.borderColor = 'var(--secondary-color)';
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.boxShadow = '0 4px 8px rgba(217, 150, 59, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'var(--primary-color)';
                                  e.target.style.borderColor = 'var(--primary-color)';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 2px 4px rgba(160, 44, 44, 0.3)';
                                }}
                              >
                                <i className="fas fa-copy" style={{
                                  background: 'none !important',
                                  border: 'none !important',
                                  boxShadow: 'none !important',
                                  transform: 'none !important',
                                  color: 'inherit',
                                  fontSize: 'inherit',
                                  pointerEvents: 'none'
                                }}></i>
                              </button>
                              <button 
                                onClick={() => handleToggleLike(game.game_state_id)}
                                className={`like-btn ${likesData[game.game_state_id]?.userLiked ? 'liked' : ''}`}
                                title={likesData[game.game_state_id]?.userLiked ? "Unlike this game" : "Like this game"}
                              >
                                <i className={`${likesData[game.game_state_id]?.userLiked ? 'fas' : 'far'} fa-heart`}></i>
                                <span className="like-count">{likesData[game.game_state_id]?.likeCount || 1}</span>
                              </button>
                            </div>
                          </div>
                          
                          {/* Game Middle Row - Description Display Area */}
                          {showAllDescriptions && game.description && (
                            <div className="game-middle-row">
                              <div className="description-display-area">
                                <div 
                                  className="game-description" 
                                  style={{
                                    margin: '0',
                                    padding: '10px',
                                    background: 'var(--background-color)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px',
                                    color: 'var(--text-color)',
                                    fontFamily: 'var(--font-secondary)',
                                    fontSize: 'var(--font-size-base)',
                                    lineHeight: '1.5',
                                    minHeight: '60px',
                                    overflow: 'auto',
                                    resize: 'vertical'
                                  }}
                                >
                                  {game.description}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="game-bottom-row">
                            {!showAllDescriptions && game.description && (
                              <p className="game-description">{game.description}</p>
                            )}
                            
                            <div className="game-details">
                              <div className="info-row" title="Created by">
                                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                  <circle cx="12" cy="7" r="4"/>
                                </svg>
                                <span className="value">@{game.author_name}</span>
                              </div>
                              <div className="info-row" title="Created Date">
                                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                                  <rect x="2" y="2" width="20" height="20" rx="2" ry="2" fill="none" stroke="currentColor"/>
                                </svg>
                                <span className="value">{formatDate(game.created_at)}</span>
                              </div>
                              <div className="info-row" title="Last Updated">
                                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
                                  <path d="M21 3v5h-5"/>
                                </svg>
                                <span className="value">{formatDate(game.updated_at)}</span>
                              </div>
                              <div className="info-row" title="Public Game">
                                <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                  <circle cx="12" cy="12" r="10"/>
                                  <line x1="2" y1="12" x2="22" y2="12"/>
                                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                                <span className="value">PUBLIC</span>
                              </div>
                              {metadata.layerCount !== undefined && (
                                <div className="info-row" title="Number of Layers">
                                  <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                    <rect x="7" y="7" width="10" height="10" rx="1" ry="1"/>
                                    <rect x="10" y="10" width="4" height="4" rx="1" ry="1"/>
                                  </svg>
                                  <span className="value">{metadata.layerCount}</span>
                                </div>
                              )}
                              {metadata.containerCount !== undefined && (
                                <div className="info-row" title="Number of Containers">
                                  <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                    <path d="M9 9h6v6H9z"/>
                                    <path d="M12 3v18"/>
                                    <path d="M3 12h18"/>
                                  </svg>
                                  <span className="value">{metadata.containerCount}</span>
                                </div>
                              )}
                              {metadata.estimatedSize && (
                                <div className="info-row" title="File Size">
                                  <svg className="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14,2 14,8 20,8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                    <polyline points="10,9 9,9 8,9"/>
                                  </svg>
                                  <span className="value">{formatFileSize(metadata.estimatedSize)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div 
                          className={`game-image-column ${expandedImages.has(game.game_state_id) ? 'expanded' : ''}`}
                          onClick={() => handleToggleImageExpand(game.game_state_id)}
                        >
                          <div 
                            className="game-image-placeholder"
                            style={game.thumbnail_url && game.thumbnail_url.includes('http') ? { backgroundImage: `url(${game.thumbnail_url})` } : {}}
                          >
                                                          {(!game.thumbnail_url || !game.thumbnail_url.includes('http')) && (
                              <svg className="placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21,15 16,10 5,21"/>
                              </svg>
                            )}
                            {game.thumbnail_url && (
                              <div className="image-overlay">
                                <span>Click to {expandedImages.has(game.game_state_id) ? 'collapse' : 'expand'}</span>
                              </div>
                            )}
                            {!expandedImages.has(game.game_state_id) && (
                              <div className="expand-hint">
                                <i className="fas fa-expand"></i>
                              </div>
                            )}
                            {expandedImages.has(game.game_state_id) && (
                              <div className="collapse-hint">
                                <i className="fas fa-times"></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
            )}

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="pagination" style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                marginTop: '20px',
                padding: '20px'
              }}>
                <button 
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '2px solid var(--primary-color)',
                    background: page <= 1 ? 'var(--surface-color)' : 'var(--primary-color)',
                    color: 'var(--text-color)',
                    cursor: page <= 1 ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-primary)',
                    fontWeight: '600',
                    opacity: page <= 1 ? 0.5 : 1
                  }}
                >
                  ← Previous
                </button>
                
                <span style={{
                  color: 'var(--text-color)',
                  fontFamily: 'var(--font-secondary)',
                  fontSize: 'var(--font-size-base)'
                }}>
                  Page {page} of {pagination.pages}
                </span>
                
                  <button 
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= pagination.pages}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: '2px solid var(--primary-color)',
                    background: page >= pagination.pages ? 'var(--surface-color)' : 'var(--primary-color)',
                    color: 'var(--text-color)',
                    cursor: page >= pagination.pages ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-primary)',
                    fontWeight: '600',
                    opacity: page >= pagination.pages ? 0.5 : 1
                  }}
                >
                  Next →
                  </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Copy Game Modal */}
      {showCopyModal && (
        <div 
          className="modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={handleCopyModalCancel}
        >
          <div 
            className="copy-modal"
            style={{
              background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%)',
              color: 'var(--text-color)',
              padding: 'var(--spacing-xl)',
              borderRadius: '12px',
              border: '2px solid var(--accent-color)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.5)',
              fontFamily: 'var(--font-primary)',
              fontWeight: '600',
              textAlign: 'center',
              maxWidth: '400px',
              width: '90%',
              animation: 'fadeInScale 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
              <i className="fas fa-copy" style={{ color: 'var(--accent-color)', fontSize: '24px' }}></i>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Copy Game to Your Collection</h3>
            </div>
            
            <p style={{ margin: '0 0 20px 0', fontFamily: 'var(--font-secondary)', fontSize: '14px', opacity: '0.9' }}>
              Enter a name for your copy of "<strong>{copyGameData?.gameName}</strong>":
            </p>
            
            <input
              type="text"
              value={copyGameName}
              onChange={(e) => setCopyGameName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCopyModalSubmit();
                }
                if (e.key === 'Escape') {
                  handleCopyModalCancel();
                }
              }}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--accent-color)',
                borderRadius: '6px',
                fontSize: '16px',
                fontFamily: 'var(--font-primary)',
                backgroundColor: 'var(--background-color)',
                color: 'var(--text-color)',
                marginBottom: '20px',
                outline: 'none'
              }}
              placeholder="Enter game name..."
              autoFocus
            />
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={handleCopyModalSubmit}
                disabled={!copyGameName.trim()}
                style={{
                  background: copyGameName.trim() ? 'var(--accent-color)' : 'var(--surface-color)',
                  color: copyGameName.trim() ? 'var(--background-color)' : 'var(--text-muted)',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontFamily: 'var(--font-primary)',
                  fontWeight: '600',
                  cursor: copyGameName.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="fas fa-copy" style={{ marginRight: '8px' }}></i>
                Copy Game
              </button>
              
              <button
                onClick={handleCopyModalCancel}
                style={{
                  background: 'transparent',
                  color: 'var(--text-color)',
                  border: '1px solid var(--text-color)',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontFamily: 'var(--font-primary)',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage; 